import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { modelsApi, brandsApi } from '../lib/api'
import { useLanguage } from '../contexts/LanguageContext'

const languageLabels = {
  az: 'Azərbaycan',
  ru: 'Русский',
  en: 'English',
  ko: '한국어',
  kk: 'Қазақша',
  ky: 'Кыргызча'
}

export default function ModelForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm({
    defaultValues: {
      manufacturer_id: '',
      name_az: '',
      name_ru: '',
      name_en: '',
      name_ko: '',
      name_kk: '',
      name_ky: ''
    }
  })

  // Fetch brands for selection
  const { data: brandsData } = useQuery(
    'brands-for-model-form',
    () => brandsApi.getAll({ limit: 100 }),
    {
      select: (data) => data.data?.results || []
    }
  )

  // Fetch model data for editing
  const { data: modelData, isLoading: isLoadingModel } = useQuery(
    ['model', id],
    () => modelsApi.getById(id),
    {
      enabled: isEdit,
      select: (data) => data.data?.data,
      onSuccess: (data) => {
        if (data) {
          setValue('manufacturer_id', data.manufacturer_id)
          setValue('name_az', data.name_az)
          setValue('name_ru', data.name_ru)
          setValue('name_en', data.name_en)
          setValue('name_ko', data.name_ko)
          setValue('name_kk', data.name_kk || data.name_ru)
          setValue('name_ky', data.name_ky || data.name_ru)
        }
      }
    }
  )

  const createMutation = useMutation(
    (data) => modelsApi.create(data),
    {
      onSuccess: () => {
        toast.success('Model created successfully')
        queryClient.invalidateQueries(['models'])
        navigate('/models')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create model')
      }
    }
  )

  const updateMutation = useMutation(
    (data) => modelsApi.update(id, data),
    {
      onSuccess: () => {
        toast.success('Model updated successfully')
        queryClient.invalidateQueries(['models'])
        navigate('/models')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update model')
      }
    }
  )

  const onSubmit = (data) => {
    if (isEdit) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  if (isEdit && isLoadingModel) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-96" />
        </div>
        <Card>
          <CardHeader>
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
                  <div className="h-10 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Model' : 'Add New Model'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {isEdit ? 'Update model information' : 'Add a new car model'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Model Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label">Brand *</label>
                <Select
                  {...register('manufacturer_id', { required: 'Brand is required' })}
                  error={errors.manufacturer_id?.message}
                >
                  <option value="">Select a brand</option>
                  {brandsData?.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div></div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Names in Different Languages</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(languageLabels).map(([lang, label]) => (
                  <div key={lang}>
                    <label className="label">
                      Name ({label}) *
                      {lang === currentLanguage && (
                        <span className="ml-2 text-xs text-primary-600 font-medium">(Current Language)</span>
                      )}
                    </label>
                    <Input
                      placeholder={`Enter name in ${label}`}
                      {...register(`name_${lang}`, { required: `${label} name is required` })}
                      error={errors[`name_${lang}`]?.message}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/models')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {isEdit ? 'Update Model' : 'Create Model'}
          </Button>
        </div>
      </form>
    </div>
  )
}
